import React from 'react';

import {useGoToDeclaration} from '../../../../../hooks/use-go-to-declaration';
import {isDocumentBeingEdited} from '../../../../../utils/document';
import {isReadOnlyOntology} from '../../../../../utils/ontology';
import {DocumentEditorHeader} from '../document-editor-header';

import {Editor} from './editor';
import {EditorWrapper} from './editor-wrapper';
import {FindUsagesEditorEvent} from '../../../../../../webeditor/biokb-editor-adapters';
import {RightPanelWrapper} from '../../../common/right-sidebar/right-panel-wrapper';
import {useDocumentSidebarState} from '../../../common/right-sidebar/hooks/use-document-sidebar-state';
import {useHasOntologyTree} from '../../../common/right-sidebar/hooks/use-has-ontology-tree';
import {OntologyView} from '../../../common/ontology-view/ontology-view';
import {OntologyElement} from '../../../common/ontology-view/requests/use-ontology-element-query';

interface Props {
    accession: string;
    onLoadingStateChange: (loading: boolean) => void;
    document: EditModeDoc;
    findUsages: (event: FindUsagesEditorEvent) => void;
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>;
    revisionId?: string;
}

export function EditorLayout(props: Props) {
    const hasOntologyTreeProps = useHasOntologyTree({
        docSidebarState: props.rightSidebarProps,
        isExploreView: false,
        docInfo: props.document
    });
    const {goToDeclaration, focusOntologyElement, focusedOntologyNode} = useGoToDeclaration(
        props.accession,
        props.revisionId,
        props.rightSidebarProps,
        true
    );

    const isDocBeingEdited = isDocumentBeingEdited(props.document) && !props.revisionId;

    return (
        <RightPanelWrapper
            docInfo={props.document}
            focusNode={focusOntologyElement}
            focusedTreeNode={focusedOntologyNode || undefined}
            rightSidebarProps={props.rightSidebarProps}
            hasOntologyTreeProps={hasOntologyTreeProps}
        >
            {isReadOnlyOntology(props.document) ? (
                <EditorWrapper>
                    <OntologyView
                        mode="edit"
                        document={props.document}
                        nodeId={focusedOntologyNode}
                        onFindUsages={props.findUsages}
                        onNavigate={goToDeclaration}
                    >
                        {(ontologyElementInfo?: OntologyElement) => (
                            <DocumentEditorHeader
                                ontologyElementInfo={ontologyElementInfo}
                                document={props.document}
                                isRevision={!!props.revisionId}
                            />
                        )}
                    </OntologyView>
                </EditorWrapper>
            ) : (
                <Editor
                    isDocBeingEdited={isDocBeingEdited}
                    document={props.document}
                    revisionId={props.revisionId}
                    focusedNodeId={focusedOntologyNode}
                    onFindUsages={props.findUsages}
                    onNavigate={goToDeclaration}
                    onLoadingStateChange={props.onLoadingStateChange}
                />
            )}
        </RightPanelWrapper>
    );
}
